import {
  type Filter,
  FilterId,
  FilterType,
  type ListFilter,
  type RangeFilter,
  type StaticListFilter,
  type TreeFilter,
} from '../types/filters';

/**
 * Filters that can be segmented (i.e., broken down by demographic dimensions)
 * Only metric range filters support segmentation
 * Note: Demographic filters (Ethnicity, Gender, etc.) are used AS segments, not segmented themselves
 */
const SEGMENTABLE_FILTER_IDS: ReadonlySet<FilterId> = new Set([
  // Metric range filters that support segmentation
  FilterId.ATTRITION_RATE,
  FilterId.GROWTH_RATE,
  FilterId.HEADCOUNT,
  FilterId.HIRING_RATE,
  FilterId.INFLOW,
  FilterId.OUTFLOW,
  FilterId.SALARY,
  FilterId.TENURE,

  // multi filters that support segmentation
  FilterId.MULTI_ATTRITION_RATE,
  FilterId.MULTI_GROWTH_RATE,
  FilterId.MULTI_HIRING_RATE,
  FilterId.MULTI_HEADCOUNT,
  FilterId.MULTI_INFLOW,
  FilterId.MULTI_OUTFLOW,
  FilterId.MULTI_SALARY,
  FilterId.MULTI_TENURE,
]);

/**
 * Filter types that support segmentation
 */
const SEGMENTABLE_FILTER_TYPES: ReadonlySet<FilterType> = new Set([
  FilterType.TREE,
  FilterType.LIST,
  FilterType.STATIC_LIST,
  FilterType.RANGE,
  FilterType.SEGMENTED,
]);

/**
 * Check if a filter supports segmentation
 */
export function canFilterBeSegmented(filter: Filter): boolean {
  // Must be a segmentable type
  if (!SEGMENTABLE_FILTER_TYPES.has(filter.type)) {
    return false;
  }

  // Must be in the list of segmentable filter IDs
  return SEGMENTABLE_FILTER_IDS.has(filter.id);
}

/**
 * Type guard to check if a filter is one of the segmentable filter types
 */
export function isSegmentableFilterType(
  filter: Filter
): filter is TreeFilter | ListFilter | StaticListFilter | RangeFilter {
  return (
    filter.type === FilterType.TREE ||
    filter.type === FilterType.LIST ||
    filter.type === FilterType.STATIC_LIST ||
    filter.type === FilterType.RANGE
  );
}

/**
 * Get all filter IDs that support segmentation
 */
export function getSegmentableFilterIds(): FilterId[] {
  return Array.from(SEGMENTABLE_FILTER_IDS);
}
